import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { LayoutService } from '@app/layout/service/layout.service';
import { NotificationControlService } from '@app/modules/notifications/application/notification-control/notification-control.service';
import { ApiNotificationService } from '@core/services/notifications/api-notification.service';
import { EnvironmentService } from '@core/services/environment/environment.service';
import { SessionService } from '@core/services/session/session.service';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { SkeletonModule } from 'primeng/skeleton';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { TooltipModule } from 'primeng/tooltip';
import { AppBreadcrumb } from './app.breadcrumb';
import { getAvatarColorByKey } from '@modules/shared/domain/functions/get-random-color';
import { resolveNotificationLink } from '@core/services/notifications/notification-link.util';
import { DemoService } from '@core/services/demo/demo.service';

@Component({
  selector: '[app-topbar]',
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    StyleClassModule,
    TooltipModule,
    AppBreadcrumb,
    InputTextModule,
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    RippleModule,
    BadgeModule,
    OverlayBadgeModule,
    AvatarModule,
    SkeletonModule,
  ],
  template: ` <div class="layout-topbar">
    <div class="topbar-left">
      <a tabindex="0" #menubutton type="button" class="menu-button" (click)="onMenuButtonClick()">
        <i class="pi pi-bars"></i>
      </a>
      <img class="horizontal-logo" src="./icons/logo-d2d.svg" alt="logo" />
      <span class="topbar-separator"></span>
      <div app-breadcrumb></div>
      <a routerLink="/">
        <img class="mobile-logo" src="./icons/logo-d2d.svg" alt="logo" />
      </a>
    </div>

    <div class="topbar-right">
      <ul class="topbar-menu">
        <li class="right-sidebar-item">
          <a
            class="right-sidebar-button"
            routerLink="demo"
            pTooltip="Lista de demos"
            tooltipPosition="bottom"
            aria-label="Lista de demos"
          >
            <i class="pi pi-play"></i>
          </a>
        </li>
        <!-- <li class="right-sidebar-item">
          <a class="right-sidebar-button" (click)="onConfigButtonClick()">
            <i class="pi pi-sliders-h"></i>
          </a>
        </li>-->
        <li class="right-sidebar-item static sm:relative">
          <a
            class="right-sidebar-button relative z-50"
            pStyleClass="@next"
            enterFromClass="hidden"
            enterActiveClass="animate-scalein"
            leaveActiveClass="animate-fadeout"
            leaveToClass="hidden"
            [hideOnOutsideClick]="true"
          >
            @if (apiNotifications.hasUnread()) {
              <span class="w-2 h-2 rounded-full bg-red-500 absolute top-2 right-2.5"></span>
            }
            <i class="pi pi-bell"></i>
          </a>
          <div
            #notificationPanel
            class="list-none m-0 py-4 px-4 rounded-3xl border border-surface absolute bg-surface-0
  dark:bg-surface-900 hidden origin-top w-[calc(100vw-2rem)] max-w-80 sm:w-[28rem] sm:max-w-[28rem] mt-2 sm:right-0 sm:left-auto right-[-1rem] left-[-8rem] z-50
  top-auto shadow-[0px_56px_16px_0px_rgba(0,0,0,0.00),0px_36px_14px_0px_rgba(0,0,0,0.01),0px_20px_12px_0px_rgba(0,0,0,0.02),0px_9px_9px_0px_rgba(0,0,0,0.03),0px_2px_5px_0px_rgba(0,0,0,0.04)]"
          >
            <div class="flex items-center gap-2 justify-between">
              <span class="text-lg font-semibold text-surface-950 dark:text-surface-0"
                >Notificaciones</span
              >
              <button
                pRipple
                class="text-surface-700 dark:text-surface-300 text-sm font-semibold whitespace-nowrap"
                (click)="onMarkAllAsRead()"
              >
                Marcar como leídas
              </button>
            </div>

            @if (apiNotifications.isInboxLoading()) {
              <ul class="flex flex-col gap-6 mt-4 max-h-[60vh] overflow-y-auto pr-1">
                @for (_ of [1, 2, 3]; track $index) {
                  <li class="flex gap-3">
                    <p-skeleton shape="circle" size="2.5rem" styleClass="shrink-0 mt-1" />
                    <div class="flex-1 space-y-2">
                      <p-skeleton width="60%" height="0.875rem" />
                      <p-skeleton width="100%" height="3rem" borderRadius="0.5rem" />
                      <p-skeleton width="35%" height="0.75rem" />
                    </div>
                  </li>
                }
              </ul>
            } @else {
              <ul class="flex flex-col gap-8 mt-4 max-h-[60vh] overflow-y-auto pr-1">
                @for (item of inboxItems(); track item.id; let i = $index) {
                  <li>
                    <a
                      [routerLink]="item.targetUrl"
                      (click)="onNotificationClick(item)"
                      class="flex gap-3 cursor-pointer no-underline text-inherit"
                    >
                      <div class="flex flex-col items-center gap-1">
                        <p-overlay-badge value="" severity="danger" class="inline-flex">
                          <p-avatar size="large">
                            <img [src]="item.image" class="rounded-lg" />
                          </p-avatar>
                        </p-overlay-badge>
                        <span class="flex-1 w-px bg-surface-200 dark:bg-surface-800"></span>
                      </div>
                      <div class="flex-1 pt-2 space-y-2 min-w-0">
                        <div class="flex items-center justify-between gap-2">
                          <span
                            class="font-semibold text-surface-950 dark:text-surface-0 truncate"
                            >{{ item.name }}</span
                          >
                          <div
                            *ngIf="!item.read"
                            class="!w-2 !h-2 rounded-full bg-green-500 shrink-0"
                          ></div>
                        </div>
                        <div
                          class="p-2 bg-surface-100 dark:bg-surface-800 border border-surface rounded-lg"
                        >
                          <p
                            class="text-sm text-surface-700 dark:text-surface-200 line-clamp-2 text-ellipsis"
                          >
                            {{ item.description }}
                          </p>
                        </div>
                        <span class="text-xs text-surface-500 dark:text-surface-400">{{
                          item.time
                        }}</span>
                      </div>
                    </a>
                  </li>
                }
              </ul>
            }
            <div class="mt-6 flex justify-center">
              <a
                routerLink="/user/notifications"
                class="px-4 py-2 rounded-md text-sm font-medium text-primary-600 dark:text-primary-400 border border-primary-200 dark:border-primary-700 hover:bg-primary-50 dark:hover:bg-primary-800 transition cursor-pointer inline-block"
                (click)="onSeeMoreClick()"
              >
                Ver más
              </a>
            </div>
          </div>
        </li>

        <li class="profile-item static sm:relative">
          <a
            pButton
            severity="secondary"
            rounded
            class="right-sidebar-button relative z-50 !pl-4"
            pStyleClass="@next"
            enterFromClass="hidden"
            enterActiveClass="animate-scalein"
            leaveActiveClass="animate-fadeout"
            leaveToClass="hidden"
            [hideOnOutsideClick]="true"
          >
            <div class="flex flex-col max-w-36 ml-1 overflow-hidden text-left">
              <div
                class="font-semibold text-xs truncate"
                title="{{ account().first_name }} {{ account().last_name }}"
              >
                {{ account().first_name }} {{ account().last_name }}
              </div>
              @if (account().roles?.length) {
                <div
                  class="text-[10px] text-primary truncate leading-tight"
                  title="{{ account().roles?.[0]?.name }}"
                >
                  {{ account().roles?.[0]?.name }}
                </div>
              }
            </div>
            @if (account().photo_url && !avatarError()) {
              <p-avatar styleClass="!w-10 !h-10 !rounded-full !bg-transparent !overflow-hidden">
                <img
                  [src]="account().photo_url"
                  [alt]="account().first_name + ' ' + account().last_name"
                  class="object-cover w-10 h-10"
                  (error)="showAvatar()"
                />
              </p-avatar>
            } @else {
              <p-avatar
                [label]="account().first_name![0] + account().last_name![0]"
                shape="circle"
                styleClass="!text-sm"
                size="large"
                [style]="getRandomAvatarColor(account().id!)"
              />
            }
          </a>
          <div
            #profilePanel
            class="list-none p-2 m-0 rounded-2xl border border-surface overflow-hidden absolute bg-surface-0 dark:bg-surface-900 hidden origin-top w-52 mt-2 right-0 z-[999] top-auto shadow-[0px_56px_16px_0px_rgba(0,0,0,0.00),0px_36px_14px_0px_rgba(0,0,0,0.01),0px_20px_12px_0px_rgba(0,0,0,0.02),0px_9px_9px_0px_rgba(0,0,0,0.03),0px_2px_5px_0px_rgba(0,0,0,0.04)]"
          >
            <ul class="flex flex-col gap-1">
              <li>
                <a
                  class="label-small text-primary-500 dark:text-primary-400 flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-primary-500/10 transition-colors duration-150 cursor-pointer"
                  [href]="website"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i class="pi pi-globe"></i>
                  <span>Sitio web</span>
                </a>
              </li>

              <li>
                <a
                  class="label-small dark:text-surface-400 flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-emphasis transition-colors duration-150 cursor-pointer"
                  [routerLink]="['/user/profile']"
                  (click)="closeProfilePanel()"
                >
                  <i class="pi pi-user"></i>
                  <span>Perfil</span>
                </a>
              </li>
              <li>
                <a
                  class="label-small dark:text-surface-400 flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-emphasis transition-colors duration-150 cursor-pointer"
                  [routerLink]="['/user/settings']"
                  (click)="closeProfilePanel()"
                >
                  <i class="pi pi-cog"></i>
                  <span>Ajustes</span>
                </a>
              </li>
              <li>
                <a
                  class="label-small dark:text-surface-400 flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-emphasis transition-colors duration-150 cursor-pointer"
                  [routerLink]="['/user/calendar']"
                  (click)="closeProfilePanel()"
                >
                  <i class="pi pi-calendar"></i>
                  <span>Calendario</span>
                </a>
              </li>
              <hr />
              <li>
                <a
                  class="label-small text-blue-500 dark:blue-red-400 flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-blue-500/10 transition-colors duration-150 cursor-pointer"
                  (click)="changeAccount()"
                >
                  <i class="pi pi-arrow-right-arrow-left"></i>
                  <span>Cambiar cuenta</span>
                </a>
              </li>
              <li>
                <a
                  class="label-small text-red-500 dark:text-red-400 flex gap-2 py-2 px-2.5 rounded-lg items-center hover:bg-red-500/10 transition-colors duration-150 cursor-pointer"
                  (click)="logout()"
                >
                  <i class="pi pi-power-off"></i>
                  <span>Cerrar sesión</span>
                </a>
              </li>
            </ul>
          </div>
        </li>
        <!-- <li class="right-sidebar-item">
          <a
            tabindex="0"
            class="right-sidebar-button"
            (click)="showRightMenu()"
          >
            <i class="pi pi-align-right"></i>
          </a>
        </li> -->
      </ul>
    </div>
  </div>`,
})
export class AppTopbar implements OnInit, OnDestroy {
  private readonly layoutService = inject(LayoutService);
  private readonly demoService = inject(DemoService);
  private readonly notificationService = inject(NotificationControlService);
  readonly apiNotifications = inject(ApiNotificationService);
  private readonly sessionService = inject(SessionService);
  private readonly router = inject(Router);
  private readonly environmentService = inject(EnvironmentService);

  isDarkTheme = computed(() => this.layoutService.isDarkTheme());

  @ViewChild('menubutton') menuButton!: ElementRef;
  @ViewChild('notificationPanel') notificationPanel?: ElementRef<HTMLElement>;
  @ViewChild('profilePanel') profilePanel?: ElementRef<HTMLElement>;

  demos = computed(() => this.demoService.demos());

  account = computed(() => this.sessionService.session()!);
  readonly avatarError = signal(false);

  ngOnInit(): void {
    this.apiNotifications.startPolling();
  }

  ngOnDestroy(): void {
    this.apiNotifications.stopPolling();
  }

  logout() {
    this.sessionService.logout();
  }

  get website() {
    return this.environmentService.WEBSITE_URL;
  }

  changeAccount() {
    this.notificationService.launchNotification(
      'Sesión cambiada',
      'Has cambiado de cuenta exitosamente',
    );
    this.router.navigate(['/auth/change-account']);
  }

  inboxItems = computed(() =>
    this.apiNotifications.inboxNotifications().map((n) => ({
      id: n.id,
      image: this._getCategoryImage(n.category),
      name: n.title,
      description: n.message ?? '',
      time: this.apiNotifications.formatRelativeTime(n.created_at),
      read: n.is_read,
      link: n.link,
      resource_type: n.resource_type,
      resource_id: n.resource_id,
      targetUrl: resolveNotificationLink(n) ?? '/user/notifications',
    })),
  );

  private _getCategoryImage(category: string): string {
    switch (category) {
      case 'client':
        return '/icons/logo-d2d.svg';
      case 'user':
        return '/icons/logo-d2d.svg';
      default:
        return '/icons/logo-d2d.svg';
    }
  }

  onMenuButtonClick() {
    this.layoutService.onMenuToggle();
  }

  showRightMenu() {
    this.layoutService.toggleRightMenu();
  }

  // onConfigButtonClick() {
  //   this.layoutService.showConfigSidebar();
  // }

  closeNotificationPanel(): void {
    if (this.notificationPanel?.nativeElement) {
      this.notificationPanel.nativeElement.classList.add('hidden');
    }
  }

  onSeeMoreClick() {
    this.closeNotificationPanel();
  }

  closeProfilePanel(): void {
    if (this.profilePanel?.nativeElement) {
      this.profilePanel.nativeElement.classList.add('hidden');
    }
  }

  showAvatar() {
    this.avatarError.set(true);
  }

  onNotificationClick(item: any) {
    if (!item.read) {
      this.apiNotifications.markAsReadAndRefresh(item.id);
    }
    this.closeNotificationPanel();
  }

  onMarkAllAsRead(): void {
    this.apiNotifications.markAllAsReadAndRefresh();
  }

  getRandomAvatarColor = (id: number) => getAvatarColorByKey(id.toString(), 'bright');
}
